import React, { useEffect, useState } from "react";
import {
  TrendingUp, Users, HeartPulse, Award, BellRing, Loader2, CheckCircle2, CalendarCheck,
} from "lucide-react";
import api from "../../lib/api";
import { PageTitle, Card, Badge, Loading, fmtDate } from "./ui";

function Stat({ icon: Icon, label, value, suffix, accent }) {
  return (
    <Card>
      <div className="flex items-center gap-2 text-gold-deep">
        <Icon size={18} />
        <span className="font-sans text-xs font-semibold uppercase tracking-widest">{label}</span>
      </div>
      <p className={`mt-4 font-display text-5xl font-semibold ${accent || "text-sapphire-deep"}`}>
        {value}
        {suffix && <span className="ml-1 font-sans text-lg text-ink/50">{suffix}</span>}
      </p>
    </Card>
  );
}

function AttendanceBar({ rate }) {
  const color = rate >= 80 ? "bg-emerald" : rate >= 60 ? "bg-gold" : "bg-garnet";
  return (
    <div className="flex items-center gap-2">
      <div className="h-2 w-28 overflow-hidden rounded-full bg-sapphire-deep/10">
        <div className={`h-full rounded-full ${color}`} style={{ width: `${rate}%` }} />
      </div>
      <span className="font-sans text-xs text-ink/60">{rate}%</span>
    </div>
  );
}

export default function Engagement() {
  const [data, setData] = useState(null);
  const [selected, setSelected] = useState({});
  const [checkin, setCheckin] = useState({ state: "idle", result: null });

  const load = () => api.get("/officer/engagement").then((r) => {
    setData(r.data);
    const risk = r.data.members.filter((m) => m.status !== "Active");
    setSelected(Object.fromEntries(risk.map((m) => [m.email, true])));
  }).catch(() => setData({ error: true }));

  useEffect(() => { load(); }, []);

  if (!data) return <Loading />;
  if (data.error) return <Card><p className="text-ink/70">Unable to load engagement data.</p></Card>;

  const { summary, trend, members } = data;
  const needsAttention = members.filter((m) => m.status !== "Active");
  const chosen = Object.keys(selected).filter((e) => selected[e]);
  const maxRate = Math.max(...trend.map((t) => t.rate), 100);

  const sendCheckin = async () => {
    setCheckin({ state: "sending", result: null });
    try {
      const { data: res } = await api.post("/officer/almoner/checkin", { emails: chosen });
      setCheckin({ state: "done", result: res });
    } catch {
      setCheckin({ state: "error", result: null });
    }
  };

  return (
    <div data-testid="engagement-page">
      <PageTitle eyebrow="Officers · Secretary · Almoner · Worshipful Master"
        title="Member Engagement"
        sub={`Participation insights across the last ${summary.meetings_counted} meetings.`} />

      {/* KPI cards */}
      <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
        <Stat icon={TrendingUp} label="Avg Attendance" value={summary.avg_attendance_rate} suffix="%" />
        <Stat icon={Award} label="Avg Engagement" value={summary.avg_engagement} suffix="/100" />
        <Stat icon={Users} label="Active Brethren" value={summary.active} accent="text-emerald" />
        <Stat icon={HeartPulse} label="Need Attention" value={summary.at_risk + summary.dormant} accent="text-garnet" />
      </div>

      {/* Trend */}
      <Card className="mt-6">
        <div className="flex items-center gap-2">
          <CalendarCheck size={18} className="text-gold-deep" />
          <h3 className="font-display text-xl font-semibold text-sapphire-deep">Attendance by Meeting</h3>
        </div>
        <div className="mt-6 flex items-end gap-3" style={{ height: 180 }} data-testid="attendance-trend">
          {trend.map((t, i) => (
            <div key={i} className="flex flex-1 flex-col items-center justify-end gap-2">
              <span className="font-sans text-xs font-semibold text-sapphire-deep">{t.rate}%</span>
              <div className="w-full rounded-t-md bg-gradient-to-t from-sapphire-deep to-sapphire-mid transition-all"
                style={{ height: `${(t.rate / maxRate) * 130}px` }} title={`${t.attended}/${t.total}`} />
              <span className="font-sans text-[10px] text-ink/50">{fmtDate(t.date).replace(/ \d{4}$/, "")}</span>
            </div>
          ))}
        </div>
      </Card>

      {/* Almoner check-in */}
      <div className="mt-8">
        <div className="mb-4 flex items-center gap-2">
          <BellRing size={18} className="text-gold-deep" />
          <h2 className="font-display text-2xl font-semibold text-sapphire-deep">Almoner — Brethren Needing a Check-in</h2>
        </div>
        <Card data-testid="almoner-panel">
          {needsAttention.length === 0 ? (
            <p className="font-sans text-sm text-ink/60">Every Brother has attended recently — nothing to flag.</p>
          ) : (
            <>
              <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                <p className="font-sans text-sm text-ink/70">
                  {needsAttention.length} Brother(en) have missed recent meetings. Select who should receive a caring note.
                </p>
                <button
                  onClick={sendCheckin}
                  disabled={checkin.state === "sending" || chosen.length === 0}
                  className="flex items-center justify-center gap-2 rounded-full bg-sapphire-deep px-6 py-3 font-sans text-sm font-semibold uppercase tracking-widest text-parchment transition hover:bg-sapphire-mid disabled:opacity-40"
                  data-testid="almoner-checkin-btn"
                >
                  {checkin.state === "sending" ? <Loader2 size={15} className="animate-spin" /> : <BellRing size={15} />}
                  {checkin.state === "sending" ? "Sending…" : `Send Check-in (${chosen.length})`}
                </button>
              </div>

              {checkin.state === "done" && checkin.result && (
                <div className="mt-5 flex items-start gap-2 rounded-xl bg-emerald/8 p-4 font-sans text-sm text-emerald" data-testid="almoner-checkin-result">
                  <CheckCircle2 size={18} className="mt-0.5 shrink-0" />
                  <span>Sent {checkin.result.sent} of {checkin.result.attempted} caring note(s).
                    {!checkin.result.email_configured && " (Email not configured — none actually delivered.)"}</span>
                </div>
              )}

              <div className="mt-5 divide-y divide-sapphire-deep/10">
                {needsAttention.map((m) => (
                  <label key={m.email} className="flex cursor-pointer items-center justify-between py-3" data-testid={`almoner-row-${m.email}`}>
                    <div className="flex items-center gap-3">
                      <input
                        type="checkbox"
                        checked={!!selected[m.email]}
                        onChange={(e) => setSelected((s) => ({ ...s, [m.email]: e.target.checked }))}
                        className="h-4 w-4 accent-sapphire-deep"
                      />
                      <div>
                        <p className="font-display text-lg text-sapphire-deep">{m.name}</p>
                        <p className="font-sans text-xs text-ink/50">
                          {m.consecutive_absences} consecutive absence(s) · last seen {m.last_attended ? fmtDate(m.last_attended) : "never"}
                        </p>
                      </div>
                    </div>
                    <Badge>{m.status}</Badge>
                  </label>
                ))}
              </div>
            </>
          )}
        </Card>
      </div>

      {/* Member table */}
      <div className="mt-8">
        <h2 className="mb-4 font-display text-2xl font-semibold text-sapphire-deep">Engagement by Member</h2>
        <Card className="overflow-x-auto">
          <table className="w-full min-w-[720px] text-left" data-testid="engagement-table">
            <thead>
              <tr className="border-b border-sapphire-deep/10 font-sans text-xs uppercase tracking-widest text-ink/50">
                <th className="pb-3 pr-4">Brother</th>
                <th className="pb-3 pr-4">Attendance</th>
                <th className="pb-3 pr-4">Meetings</th>
                <th className="pb-3 pr-4">Last Seen</th>
                <th className="pb-3 pr-4">Score</th>
                <th className="pb-3">Status</th>
              </tr>
            </thead>
            <tbody>
              {members.map((m) => (
                <tr key={m.email} className="border-b border-sapphire-deep/5">
                  <td className="py-4 pr-4">
                    <p className="font-display text-lg text-sapphire-deep">{m.name}</p>
                    <p className="font-sans text-xs text-gold-deep">{m.rank}</p>
                  </td>
                  <td className="py-4 pr-4"><AttendanceBar rate={m.attendance_rate} /></td>
                  <td className="py-4 pr-4 font-sans text-sm text-ink/70">{m.attended}/{m.total}</td>
                  <td className="py-4 pr-4 font-sans text-sm text-ink/70">{m.last_attended ? fmtDate(m.last_attended) : "—"}</td>
                  <td className="py-4 pr-4"><span className="font-display text-xl font-semibold text-sapphire-deep">{m.engagement_score}</span></td>
                  <td className="py-4"><Badge>{m.status}</Badge></td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      </div>
    </div>
  );
}
